<template>
  <div class="min-h-screen bg-gradient-to-br from-[#25274D] via-[#464866] to-[#25274D] flex items-center justify-center p-4 relative overflow-hidden">
    <div
      v-for="(star, i) in stars"
      :key="i"
      class="absolute rounded-full bg-white animate-pulse"
      :style="{
        left: `${star.x}%`,
        top: `${star.y}%`,
        width: `${star.size}px`,
        height: `${star.size}px`,
        opacity: star.opacity,
        animationDuration: `${star.duration}s`
      }"/>

    <div class="max-w-2xl w-full relative z-10">
      <div class="bg-[#464866]/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-[#BABBC4]/20 p-8 md:p-12">
        <div class="relative w-32 h-32 mx-auto mb-8">
          <div class="absolute inset-0 rounded-full border-2 border-[#2E9CCA]/40" />
          <div class="absolute inset-2 rounded-full border border-[#2E9CCA]/25" />
          
          <div
            class="absolute w-3 h-3 bg-[#2E9CCA] rounded-full shadow-lg"
            :style="{
              left: '50%',
              top: '50%',
              transform: `rotate(${orbitAngle}deg) translateX(60px) rotate(-${orbitAngle}deg)`,
              marginLeft: '-6px',
              marginTop: '-6px',
              boxShadow: '0 0 20px rgba(46, 156, 202, 0.5)'
            }"
          />
          
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="bg-gradient-to-br from-[#2E9CCA] to-[#29648A] rounded-full p-6 shadow-lg">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="w-12 h-12 text-white" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="text-center mb-6">
          <h1 class="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2E9CCA] to-[#29648A] mb-4">
            403
          </h1>
          <h2 class="text-3xl font-semibold text-white mb-4">
            Access Restricted
          </h2>
        </div>

        <div class="bg-[#25274D]/60 rounded-lg p-6 mb-8 border border-[#2E9CCA]/30">
          <div class="flex items-start gap-3">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              class="w-6 h-6 text-[#2E9CCA] flex-shrink-0 mt-0.5" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div>
              <p class="text-[#BABBC4] leading-relaxed mb-3">
                <span class="font-semibold text-white">Clearance Level Insufficient.</span> Your current permissions do not grant access to this resource.
              </p>
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            @click="handleGoBack"
            class="flex items-center justify-center gap-2 px-6 py-3 bg-[#464866] hover:bg-[#464866]/80 text-white rounded-lg transition-colors duration-200 font-medium"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              class="w-5 h-5" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Go Back
          </button>
          <button
            @click="handleGoHome"
            class="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2E9CCA] to-[#29648A] hover:from-[#2E9CCA]/90 hover:to-[#29648A]/90 text-white rounded-lg transition-all duration-200 font-medium shadow-lg"
            :style="{ boxShadow: '0 10px 25px rgba(46, 156, 202, 0.3)' }"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              class="w-5 h-5" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
            >
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            Home
          </button>
        </div>

        <div class="mt-8 pt-6 border-t border-[#BABBC4]/20 text-center">
          <p class="text-[#BABBC4]/60 text-sm">
            Error Code: <span class="font-mono text-[#BABBC4]">403-FORBIDDEN</span>
          </p>
        </div>
      </div>
    </div>

    <div class="absolute inset-0 opacity-5 pointer-events-none" 
         style="background-image: linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 50px 50px;" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

const stars = ref([]);
const orbitAngle = ref(0);
let orbitInterval = null;
const router = useRouter()

onMounted(() => {
  stars.value = Array.from({ length: 50 }, () => ({
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 1,
    opacity: Math.random() * 0.5 + 0.3,
    duration: 2 + Math.random() * 2
  }));

  orbitInterval = setInterval(() => {
    orbitAngle.value = (orbitAngle.value + 1) % 360;
  }, 50);
});

onUnmounted(() => {
  if (orbitInterval) {
    clearInterval(orbitInterval);
  }
});

const handleGoBack = () => {
  window.history.back();
};

const handleGoHome = () => {
  router.push('/')
};
</script>

